import type {
    ContextMeshSourceCategoryKey,
    ContextMeshSourceDiagnostic
} from "./contextMeshAnalysis";

type SourceLabels = {
    sourceSingular: string;
    sourcePlural: string;
};

const SOURCE_LABELS: Record<ContextMeshSourceCategoryKey, SourceLabels> = {
    external: {
        sourceSingular: "ticketing system",
        sourcePlural: "ticketing systems"
    },
    file: {
        sourceSingular: "file",
        sourcePlural: "files"
    },
    code: {
        sourceSingular: "repository",
        sourcePlural: "repositories"
    }
};

function counted(count: number, singular: string, plural: string): string {
    return `${count} ${count === 1 ? singular : plural}`;
}

export function sourceDiagnosticCounts(
    category: ContextMeshSourceCategoryKey,
    diagnostic: ContextMeshSourceDiagnostic
): string[] {
    const labels = SOURCE_LABELS[category];
    return [`${counted(diagnostic.connectedSourceCount, labels.sourceSingular, labels.sourcePlural)} connected`];
}

export function sourceDiagnosticBadge(
    category: ContextMeshSourceCategoryKey,
    diagnostic: ContextMeshSourceDiagnostic
): string {
    return sourceDiagnosticCounts(category, diagnostic)[0];
}

export function sourceDiagnosticState(
    category: ContextMeshSourceCategoryKey,
    diagnostic: ContextMeshSourceDiagnostic,
    lowScore: boolean,
    scoredSummary?: string | null
): string {
    const labels = SOURCE_LABELS[category];
    if (diagnostic.status === "not_connected") {
        return `No ${labels.sourcePlural} are connected.`;
    }
    if (diagnostic.status === "processing") {
        return scoredSummary?.trim() || "Detailed category scoring is not available.";
    }
    if (lowScore) {
        return "The connection succeeded; this low score reflects relevance and coverage, not a connection problem.";
    }
    return scoredSummary?.trim() || "Connected sources were analyzed successfully.";
}

export function sourceDiagnosticEmptyState(
    category: ContextMeshSourceCategoryKey,
    diagnostic: ContextMeshSourceDiagnostic
): string {
    const labels = SOURCE_LABELS[category];
    if (diagnostic.status === "not_connected") {
        return `No ${labels.sourcePlural} are connected for this context yet.`;
    }
    if (diagnostic.status === "processing") {
        return `${counted(diagnostic.connectedSourceCount, labels.sourceSingular, labels.sourcePlural)} connected. No report items are available for this context.`;
    }
    return `${counted(diagnostic.connectedSourceCount, labels.sourceSingular, labels.sourcePlural)} connected. No report items are available for this context.`;
}
